import {Component, OnDestroy, OnInit} from '@angular/core';
import {forkJoin, Observable, Subscription} from "rxjs";
import {take} from "rxjs/operators";
import {Broadcast} from "../model/broadcast/broadcast";
import {BroadcastsService} from "../services/broadcasts.service";
import {ActivatedRoute} from "@angular/router";
import {Game} from "../model/broadcast/game";
import {GamesService} from "../services/games.service";
import {LeaguesService} from "../services/leagues.service";
import {RostersService} from "../services/rosters.service";
import {UtilService} from "../services/util.service";
import {Player} from "../model/broadcast/player";
import {Team} from "../model/broadcast/team";
import {League} from "../model/league";
import {NgFor, NgIf} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {OffenseComponent} from "./offense/offense.component";
import {PitchersComponent} from "./pitchers/pitchers.component";
import {pitcherRows} from "./pitchers/pitcher-rows";
import {resolveTeamName} from "../shared/team-name";
import {DefenseComponent} from "./defense/defense.component";

@Component({
  selector: 'app-score-sheet',
  templateUrl: './score-sheet.component.html',
  styleUrls: ['./score-sheet.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, FormsModule, MatSlideToggleModule, OffenseComponent, PitchersComponent, DefenseComponent],
})
export class ScoreSheetComponent implements OnInit, OnDestroy {

  broadcast: Broadcast;
  game: Game;
  league?: League;
  showBench = false;
  private subscriptions: Subscription[] = [];
  private gameSub?: Subscription;
  private rostersLoadedForLeagueId?: string;

  constructor(
    private broadcastService: BroadcastsService,
    private gamesService: GamesService,
    private leaguesService: LeaguesService,
    private rostersService: RostersService,
    private utilService: UtilService,
    private route: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    const gameId = this.route.snapshot.paramMap.get('gameId');
    if (gameId) {
      this.gameSub = this.gamesService.getGame(gameId).subscribe(g => {
        this.game = this.normalise(g);
        this.maybeLoadRosters(g);
      });
      return;
    }
    const uid = this.route.snapshot.paramMap.get('uid');
    if (!uid) return;
    const broadcastSub = this.broadcastService.getBroadcast(uid).subscribe(b => {
      this.broadcast = b;
      if (b?.gameId) {
        this.gameSub?.unsubscribe();
        this.gameSub = this.gamesService.getGame(b.gameId).subscribe(g => {
          this.game = this.normalise(g);
          if (!this.broadcast?.fullRosters?.length) {
            this.maybeLoadRosters(g);
          }
        });
      }
    });
    this.subscriptions.push(broadcastSub);
  }

  /**
   * Fold each team's pitcher list down to one row per pitcher before anything
   * renders it — see pitcher-rows.ts for why WBSC sends the same pitcher twice.
   *
   * Applied here rather than in a getter so the arrays keep a stable identity for
   * as long as the snapshot does: `app-offense` recomputes its pitching-change
   * boundaries whenever its inputs change, and a fresh array on every
   * change-detection pass would make it redo that work continuously.
   *
   * Safe to write into the emitted object: `docData()` hands out a new plain object
   * per snapshot and nothing else holds a reference to it.
   */
  private normalise(game: Game): Game {
    if (game?.home) game.home.pitchers = pitcherRows(game.home.pitchers);
    if (game?.away) game.away.pitchers = pitcherRows(game.away.pitchers);
    return game;
  }

  // The scoresheet header showed "ZRA" where the game list shows "Žraloci
  // Ledenice", for two reasons that both live in the old lookup:
  //
  // 1. `league.teams[].id` is stored as a *number* while `Team.guid` is a string,
  //    so `t.id === guid` never matched and the league name was never reached.
  //    All 92 team ids on prod league 3356 are numbers — this lookup never worked.
  // 2. Live data repeats the IOC code in `fullName`, so the fallback to
  //    `game.away.fullName` yielded 'ZRA' rather than nothing.
  //
  // resolveTeamName handles both (string-compared ids, and a candidate equal to the
  // abbreviation is skipped), and is the same helper the game list uses, so the two
  // screens now agree. Precedence is unchanged: a broadcast's own config still wins,
  // then the league config, then live data, then the code.
  get awayName(): string {
    return resolveTeamName(this.league, this.game?.away?.guid,
      [this.broadcast?.config?.awayConfig?.fullName, this.game?.away?.fullName],
      this.game?.away?.shortName, 'Visitors');
  }

  get homeName(): string {
    return resolveTeamName(this.league, this.game?.home?.guid,
      [this.broadcast?.config?.homeConfig?.fullName, this.game?.home?.fullName],
      this.game?.home?.shortName, 'Home');
  }

  benchFor(team?: Team): { number: string; name: string }[] {
    const teamGuid = team?.guid;
    const rosters = this.broadcast?.fullRosters ?? [];
    if (!teamGuid || !rosters.length) return [];

    const usedGuids = new Set<string>();
    team?.lineup?.forEach(spot => spot.players?.forEach(p => p?.guid && usedGuids.add(p.guid)));
    team?.pitchers?.forEach(p => p?.guid && usedGuids.add(p.guid));

    return rosters
      .filter(p => p.teamGuid === teamGuid && p.guid && !usedGuids.has(p.guid))
      .map(p => ({
        number: this.utilService.getNumber(p, this.broadcast),
        name: p.name ?? '',
      }))
      .sort((a, b) => {
        const na = parseInt(a.number.replace('#', ''), 10);
        const nb = parseInt(b.number.replace('#', ''), 10);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return a.name.localeCompare(b.name);
      });
  }

  get boxscoreInnings(): number[] {
    const len = Math.max(
      9,
      this.game?.away?.scoreBoard?.length ?? 0,
      this.game?.home?.scoreBoard?.length ?? 0,
    );
    return Array.from({length: len}, (_, i) => i + 1);
  }

  private maybeLoadRosters(game: Game): void {
    const leagueId = game?.tournamentId;
    if (!leagueId || this.rostersLoadedForLeagueId === leagueId) return;

    this.rostersLoadedForLeagueId = leagueId;
    this.leaguesService.getLeague(leagueId).pipe(take(1)).subscribe(league => {
      this.league = league;
      if (!league) {
        this.rostersLoadedForLeagueId = undefined;
        return;
      }
      const homeId = game.home?.guid;
      const awayId = game.away?.guid;
      const homeLicenced = this.utilService.isLicencedForBroadcast(league, homeId);
      const awayLicenced = this.utilService.isLicencedForBroadcast(league, awayId);
      if (!homeLicenced && !awayLicenced) return;

      const fetches: Observable<Player[]>[] = [];
      if (homeLicenced && homeId) fetches.push(this.rostersService.getRoster(league.id, homeId).pipe(take(1)));
      if (awayLicenced && awayId) fetches.push(this.rostersService.getRoster(league.id, awayId).pipe(take(1)));
      if (!fetches.length) return;

      forkJoin(fetches).subscribe(rosters => {
        const merged = rosters.flat();
        this.broadcast = {
          ...(this.broadcast ?? {} as Broadcast),
          fullRosters: [...(this.broadcast?.fullRosters ?? []), ...merged],
        } as Broadcast;
      });
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.gameSub?.unsubscribe();
  }
}
